import { typedMarkingPublishingDurationOptions } from '@/features/analytics/invite-links/const/marking'
import Icon from '@/features/analytics/invite-links/ui/Marking/icon'
import { QuestionSVGIcon } from '@/shared/assets'
import { Checkbox, SelectDropdown } from '@/shared/ui'
import { DatePickerWithIcon } from '@/shared/ui/date-picker'
import { DisableTooltip } from '@/shared/ui/disable-tooltip'
import { Label } from '@/shared/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { Controller } from 'react-hook-form'
import { usePublishOptions } from './usePublishOptions'

type PublishOptionsProps = {
    markingFormData: ORDMarkingFormData
} & WorkspaceWithLinkDescriptor

export const PublishOptions: React.FC<PublishOptionsProps> = ({
    markingFormData,
    ...wwld
}) => {
    const {
        control,
        publishOptionFlag,
        canChangeDates,
        canAllowCheckbox,
        isBlockedByContract,
        isBlockedByPublisher,
        isDeletedDateDisabled,
        deletedDateTooltipMessage,
        publishOptionError,
        showError,
        handlePublishDateChange,
        handleSelectDuration,
        handleDeletedDateChange,
        handleChangeCheckboxStatus,
    } = usePublishOptions(markingFormData, wwld)

    // const handleSelectPaymentMethod = (option: any) => {
    //     setValue('paymentMethod', option.value)
    //     updateMarkingFormData.mutate({
    //         payment_method: option.value,
    //     })
    // }

    return (
        <div className="flex flex-col gap-[6px]">
            <div className="flex gap-9">
                <div className="flex items-center gap-[6px]">
                    <Icon icon="clock" checked={publishOptionFlag} />

                    <h2 className="text-sm font-semibold">
                        Параметры публикации
                    </h2>

                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionSVGIcon className="cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            <p>Информация о параметрах публикации</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>

            <div className="flex h-[68px] items-center justify-between">
                <div className="flex w-[448px] items-end gap-2">
                    <div className="relative flex w-[108px] flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Планируемая дата публикации
                        </span>

                        <DisableTooltip
                            enabled={!canChangeDates}
                            message='Если хотите отредактировать, то уберите галочку c "Токен получен" и удалите подписанный договор'
                        >
                            <Controller
                                name="publishedAt"
                                control={control}
                                render={({ field }) => (
                                    <DatePickerWithIcon
                                        selected={field.value}
                                        isDisabled={!canChangeDates}
                                        isBlue={!!markingFormData.publish_date}
                                        onChange={(date: Date | undefined) => {
                                            field.onChange(date)
                                            handlePublishDateChange(date)
                                        }}
                                        className="text-xs"
                                        error={showError(!!field.value)}
                                        dateFormat="dd.MM.yy"
                                        dropdownMode="scroll"
                                        startDate={new Date()}
                                        minDate={new Date()}
                                        maxDate={
                                            markingFormData.deleted_date ||
                                            undefined
                                        }
                                    />
                                )}
                            />
                        </DisableTooltip>
                    </div>

                    <div className="relative flex w-[158px] flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Время в ленте
                        </span>

                        <DisableTooltip
                            enabled={publishOptionFlag}
                            message="Если хотите отредактировать, то уберите галочку справа"
                        >
                            <Controller
                                name="duration"
                                control={control}
                                render={({ field }) => (
                                    <SelectDropdown
                                        disabled={publishOptionFlag}
                                        options={
                                            typedMarkingPublishingDurationOptions
                                        }
                                        onSelect={handleSelectDuration}
                                        value={field.value || 'no_duration'}
                                        className="w-[158px]"
                                        headerClassName="px-[10px] py-2"
                                        placeholder="Select Duration"
                                        labelClassName="p-0 truncate"
                                        error={showError(
                                            Boolean(
                                                field.value === 'no_duration'
                                                    ? null
                                                    : field.value
                                            )
                                        )}
                                    />
                                )}
                            />
                        </DisableTooltip>
                    </div>

                    <div className="relative flex h-[68px] w-[108px] flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Планируемая дата удаления
                        </span>
                        <DisableTooltip
                            enabled={isDeletedDateDisabled}
                            message={deletedDateTooltipMessage}
                        >
                            <Controller
                                name="deletedDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePickerWithIcon
                                        selected={field.value}
                                        isDisabled={isDeletedDateDisabled}
                                        isBlue={!!markingFormData.deleted_date}
                                        onChange={(date: Date | undefined) => {
                                            field.onChange(date)
                                            handleDeletedDateChange(date)
                                        }}
                                        className="text-xs"
                                        error={showError(!!field.value)}
                                        dateFormat="dd.MM.yy"
                                        dropdownMode="scroll"
                                        startDate={
                                            markingFormData.publish_date ||
                                            new Date()
                                        }
                                        minDate={
                                            markingFormData.publish_date ||
                                            new Date()
                                        }
                                    />
                                )}
                            />
                        </DisableTooltip>
                    </div>

                    {/* <div className="relative flex w-[108px] flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Модель оплаты
                        </span>
                        <DisableTooltip
                            enabled={publishOptionFlag}
                            message="Если хотите отредактировать, то уберите галочку справа"
                        >
                            <Controller
                                name="paymentMethod"
                                control={control}
                                render={({ field }) => (
                                    <SelectDropdown
                                        disabled={publishOptionFlag}
                                        options={typedMarkingPayOptions}
                                        onSelect={handleSelectPaymentMethod}
                                        value={field.value || 'none'}
                                        className="w-[108px]"
                                        headerClassName="px-[10px] py-2 gap-0"
                                        labelClassName="p-0 "
                                        placeholder="Pay Option"
                                        error={showError(field.value)}
                                    />
                                )}
                            />
                        </DisableTooltip>
                    </div> */}
                </div>
                <div className="flex h-[60px] w-[105px] items-end gap-[6px] pb-0">
                    <DisableTooltip
                        enabled={
                            !canAllowCheckbox ||
                            isBlockedByContract ||
                            isBlockedByPublisher
                        }
                        className="h-5"
                        message={
                            isBlockedByPublisher
                                ? 'Нельзя отжать так как издатель уже выбран'
                                : isBlockedByContract
                                  ? 'Нельзя отжать так как договор уже сформирован'
                                  : 'Пожалуйста, заполните все параметры, чтобы отметить этот пункт'
                        }
                        asChild
                    >
                        <div
                            onClick={() => {
                                if (
                                    !canAllowCheckbox ||
                                    isBlockedByContract ||
                                    isBlockedByPublisher
                                ) {
                                    handleChangeCheckboxStatus()
                                }
                            }}
                        >
                            <Checkbox
                                id="channel_allow"
                                checked={publishOptionFlag}
                                disabled={
                                    !canAllowCheckbox ||
                                    isBlockedByContract ||
                                    isBlockedByPublisher
                                }
                                onChange={handleChangeCheckboxStatus}
                                sizeVariant="big"
                                variant={publishOptionError ? 'error' : 'green'}
                                size={20}
                            />
                        </div>
                    </DisableTooltip>
                    <Label
                        htmlFor="channel_allow"
                        className="w-[79px] text-[10px] font-medium leading-3"
                    >
                        Размещение забронировано
                    </Label>
                </div>
            </div>
        </div>
    )
}
